import axios from 'axios';

// Dacă variabila lipsește sau e "undefined" → folosim /api (same-domain, pentru producție)
// Local dev → setează REACT_APP_API_URL=http://localhost:8000 în .env
let API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
if (!API_URL || API_URL === 'undefined') {
  API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api' : '/api';
} else if (!API_URL.endsWith('/api')) {
  API_URL = `${API_URL}/api`;
}

const getToken = () => localStorage.getItem('nautica_token');

const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interceptor pentru a trimite automat token-ul
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor pentru erori (în special 401 - Unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nautica_token');
      if (window.location.pathname !== '/login') {
         window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  setupPassword: (token, password) => api.post('/auth/setup-password', { token, password }),
  requestReset: (email) => api.post('/auth/request-reset', { email }),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (current_password, new_password) => api.put('/auth/change-password', { current_password, new_password }),
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }), // Acum suportă { page, limit, search, sort_by }
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resendSetup: (id) => api.post(`/users/${id}/resend-setup`),
  getWithActiveSubscriptions: (params) => api.get('/users/active-subscriptions', { params }),
  getAllChildren: () => api.get('/users/children/all'),

  // Modulul de Familie (Copii)
  addChild: (userId, data) => api.post(`/users/${userId}/children`, data),
  updateChild: (userId, childId, data) => api.put(`/users/${userId}/children/${childId}`, data),
  deleteChild: (userId, childId) => api.delete(`/users/${userId}/children/${childId}`),
};

// Plans
export const plansAPI = {
  getAll: (params) => api.get('/plans', { params }),
  getAllAdmin: () => api.get('/plans', { params: { include_inactive: true } }),
  getOne: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

// Subscriptions
export const subscriptionsAPI = {
  getAll: (params) => api.get('/subscriptions', { params }),
  getForPerson: (personId, personType) => api.get(`/subscriptions/person/${personId}`, { params: { person_type: personType } }),
  create: (data) => api.post('/subscriptions', data),
  createBatch: (data) => api.post('/subscriptions/batch', data), // Endpoint nou pt checkout rapid la familii
  delete: (id) => api.delete(`/subscriptions/${id}`),
};

// Attendance
export const attendanceAPI = {
  getAll: (params) => api.get('/attendance', { params }),
  // Noul backend accepta POST /attendance direct, cu person_id SAU qr_token in body
  recordManual: (data) => api.post('/attendance', data),
  recordQR: (qrToken, location) => api.post('/attendance', { qr_token: qrToken, location }),
  delete: (id) => api.delete(`/attendance/${id}`), // Acest delete face si "Undo" la abonament acum!
  export: (params) => api.get('/export/attendance', { params }),
  emailExport: (params) => api.post('/export/attendance/email', null, { params }),
  getMine: (params) => api.get('/attendance/mine', { params }), // Calendarul din portalul clientului
};

// Locations 
export const locationsAPI = {
  getAll: () => api.get('/locations'),
  getOne: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Coaches
export const coachesAPI = {
  getAll: () => api.get('/coaches'),
  create: (data) => api.post('/coaches', data),
  update: (id, data) => api.put(`/coaches/${id}`, data),
  delete: (id) => api.delete(`/coaches/${id}`),
};

// Blog
export const blogAPI = {
  getAll: (publishedOnly = true) => api.get('/blog', { params: { published_only: publishedOnly } }),
  getOne: (slug) => api.get(`/blog/${slug}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/blog/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Alerts
export const alertsAPI = {
  getAll: (params) => api.get('/alerts', { params }),
  getCount: () => api.get('/alerts/count'),
  markSeen: (id) => api.put(`/alerts/${id}/seen`),
  markAllSeen: () => api.put('/alerts/seen-all'),
  delete: (id) => api.delete(`/alerts/${id}`),
  deleteAllSeen: () => api.delete('/alerts/seen'),
  getMine: () => api.get('/alerts/mine'),
  markAllMineSeen: () => api.put('/alerts/mine/seen-all'),
};

// Reports
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getRevenue: (months) => api.get('/reports/revenue', { params: { months } }),
  getAttendance: (months) => api.get('/reports/attendance', { params: { months } }),
  getLocations: () => api.get('/reports/locations'),
  getHours: () => api.get('/reports/hours'),
  getRetention: (months) => api.get('/reports/retention', { params: { months } }),
  // Exports
  exportExcel: (params) => api.get('/reports/export/excel', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/reports/export/pdf', { params, responseType: 'blob' }),
};

// Contact (Smart Inbox)
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getMessages: () => api.get('/contact'),
  markRead: (id) => api.put(`/contact/${id}/read`),
};

// Maintenance
export const maintenanceAPI = {
  markInactiveUsers: () => api.post('/maintenance/mark-inactive-users'),
};

// QR Code Scanner
export const qrAPI = {
  // Sincronizat cu noul 'backend/routes/qr.py'
  validate: (token) => api.get(`/qr/scan/${token}`),
  regenerate: (personId, personType) => api.post(`/qr/regenerate/${personId}`, null, { params: { person_type: personType } }),
};

// Seed DB
export const seedAPI = {
  seed: () => api.post('/seed'),
  triggerAlerts: () => api.post('/dev/trigger/alerts'),
  triggerFeedback: () => api.post('/dev/trigger/feedback'),
  triggerWeeklyDigest: () => api.post('/dev/trigger/weekly-digest'),
  triggerMonthlyReport: () => api.post('/dev/trigger/monthly-report'),
  triggerCleanup: () => api.post('/dev/trigger/cleanup'),
};

export default api;